<template>
  <!-- DP-4478 — Hakim, 2026-08-06: every label created for the order, the items
       each one carried, and the items not shipped yet (Figma section "Multiple
       Shipping Label Creation", frames 1609 / 1581 / 1594 / 1595). -->
  <b-modal id="manage-shipping-modal" size="xl" @show="onShow" @hidden="onHidden">
    <template v-slot:modal-header="{ close }">
      <b-container>
        <div class="modal-title">
          <h3 class="text-bold text-center">Manage Shipping</h3>
          <button class="close" type="button" aria-label="Close" @click="close">
            <i class="di-remove-10"></i>
          </button>
        </div>
      </b-container>
    </template>

    <div class="container dp-ms-body">
      <div v-if="loading && !overview" class="dp-ms-loading">
        <b-spinner small /><span class="ml-2">Loading shipping…</span>
      </div>
      <b-alert v-else-if="failed && !overview" show variant="danger">
        The shipping details could not be loaded.
        <a href="#" @click.prevent="load">Try again</a>
      </b-alert>

      <template v-else-if="overview">
        <h4 class="dp-ms-heading">Shipped Items</h4>

        <div v-if="!shipments.length" class="dp-ms-empty">
          <PackageIcon :size="36" />
          <div class="mt-2">No items have been shipped yet</div>
        </div>

        <div v-for="shipment in orderedShipments" :key="shipment.id" class="dp-ms-card">
          <div class="dp-ms-card-title">
            {{ shipment.is_manual_entry ? `Shipment #${shipment.number}` : shipment.title }}
          </div>
          <div
            v-for="(item, index) in shipment.items"
            :key="`${shipment.id}-${item.order_item_id}`"
            class="dp-ms-row"
          >
            <div class="dp-ms-item">
              <div class="dp-ms-item-name">
                {{ item.name }}<span v-if="item.quantity > 1" class="dp-ms-qty">×{{ item.quantity }}</span>
              </div>
              <div class="dp-ms-item-details" v-if="details(item)">{{ details(item) }}</div>
              <div class="dp-ms-item-id">Order ID: {{ item.order_item_id }}</div>
            </div>
            <!-- Status, creator, date and label belong to the whole label, so
                 they sit on its first item only, as in the design. -->
            <template v-if="index === 0">
              <span class="dp-ms-pill dp-ms-pill--dispatched">Dispatched</span>
              <div class="dp-ms-meta">
                <div class="dp-ms-meta-label">Created by</div>
                <div class="dp-ms-meta-value">{{ creatorName(shipment) }}</div>
              </div>
              <div class="dp-ms-meta">
                <div class="dp-ms-meta-label">Created at</div>
                <div class="dp-ms-meta-value">{{ createdAt(shipment) }}</div>
              </div>
              <b-button
                class="dp-ms-label-btn"
                variant="outline-light"
                :disabled="labelLoadingId === shipment.id"
                @click="openLabel(shipment)"
              >
                <b-spinner v-if="labelLoadingId === shipment.id" small class="mr-2" />
                {{ labelButtonText(shipment) }}
              </b-button>
            </template>
          </div>

          <!-- DP-5377 (Canada manual entry): reason, notes, customer email
               status, history and the Edit / Resend actions. -->
          <div v-if="shipment.is_manual_entry" class="dp-ms-ca">
            <div class="dp-ms-ca-facts">
              <div class="dp-ms-meta">
                <div class="dp-ms-meta-label">Reason</div>
                <div class="dp-ms-meta-value">{{ shipment.shipment_reason_label }}</div>
              </div>
              <div class="dp-ms-meta dp-ms-ca-notes" v-if="shipment.internal_notes">
                <div class="dp-ms-meta-label">Notes</div>
                <div class="dp-ms-meta-value">{{ shipment.internal_notes }}</div>
              </div>
              <div class="dp-ms-meta dp-ms-ca-email">
                <div class="dp-ms-meta-label">Email</div>
                <div class="dp-ms-ca-email-status" :class="`dp-ms-ca-email-status--${shipment.email_status}`">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M14 8.5V4a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h5.5" />
                    <path d="M2.5 3.5 8 8l5.5-4.5" />
                    <path v-if="shipment.email_status === 'sent'" d="m10.5 12.5 1.5 1.5 3-3" />
                    <path v-else-if="shipment.email_status === 'queued'" d="M12.5 10.5v2l1.2 1" />
                    <path v-else d="m11 11 3 3m0-3-3 3" />
                  </svg>
                  <span>{{ emailStatusText(shipment) }}</span>
                </div>
              </div>
            </div>
            <div class="dp-ms-ca-actions" v-if="canManageCanada">
              <b-button size="sm" variant="outline-light" class="dp-ms-label-btn" @click="openCa('edit', shipment)">
                Edit Tracking
              </b-button>
              <b-button
                size="sm"
                :variant="shipment.email_status === 'failed' ? 'danger' : 'outline-light'"
                :class="{ 'dp-ms-label-btn': shipment.email_status !== 'failed' }"
                @click="openCa('resend', shipment)"
              >
                {{ shipment.email_status === 'failed' ? 'Retry Email' : 'Resend Email' }}
              </b-button>
              <b-button
                v-if="shipment.events && shipment.events.length"
                size="sm"
                variant="link"
                @click="toggleHistory(shipment.id)"
              >
                {{ openHistory[shipment.id] ? 'Hide history' : 'View history' }}
              </b-button>
            </div>
            <ul v-if="openHistory[shipment.id]" class="dp-ms-ca-history">
              <li v-for="event in shipment.events" :key="event.id">
                <span class="dp-ms-ca-date">{{ formatDate(event.created_at) }}</span>
                {{ eventText(event) }}
              </li>
            </ul>
          </div>
        </div>

        <div class="dp-ms-divider"></div>

        <!-- "All Items" until the first label exists, "Pending Shipment" after
             (frames 1609 vs 1581). -->
        <h4 class="dp-ms-heading">{{ shipments.length ? 'Pending Shipment' : 'All Items' }}</h4>

        <div v-if="!items.length" class="dp-ms-empty">
          <PackageIcon :size="36" />
          <div class="mt-2">This order has no items to ship</div>
        </div>
        <div v-else-if="!pendingItems.length" class="dp-ms-empty">
          <PackageIcon :size="36" />
          <div class="mt-2">All items dispatched</div>
        </div>
        <div v-else class="dp-ms-list">
          <div v-for="item in pendingItems" :key="item.order_item_id" class="dp-ms-row">
            <div class="dp-ms-item">
              <div class="dp-ms-item-name">
                {{ item.name }}<span v-if="item.quantity > 1" class="dp-ms-qty">×{{ item.quantity }}</span>
              </div>
              <div class="dp-ms-item-details" v-if="details(item)">{{ details(item) }}</div>
              <div class="dp-ms-item-id">Order ID: {{ item.order_item_id }}</div>
            </div>
            <span class="dp-ms-pill dp-ms-pill--pending">Pending Shipment</span>
          </div>
        </div>
      </template>
    </div>

    <template v-slot:modal-footer="{ close }">
      <div class="flex-fill m-0">
        <div class="container">
          <div class="d-flex justify-content-between">
            <b-button class="dp-ms-close" variant="outline-light" @click="close()">Close</b-button>
            <b-button
              v-if="canManageCanada && overview"
              variant="primary"
              class="dp-ms-create"
              @click="openCa(shipments.length ? 'another' : 'first')"
            >
              <PackageIcon class="mr-2" />{{ shipments.length ? 'Add Another Shipment' : 'Add Tracking' }}
            </b-button>
            <b-button
              v-else-if="canCreateShippingOrder"
              variant="primary"
              class="dp-ms-create"
              @click="createShippingOrder"
            >
              <PackageIcon class="mr-2" />Create Shipping Order
            </b-button>
          </div>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import apiOrders from '@/api/orders';
import PackageIcon from '@/views/modals/ManageShipping/PackageIcon.vue';
import { itemDetails } from '@/views/modals/ManageShipping/shipping-format';

export default {
  name: 'ManageShippingModal',
  components: {
    PackageIcon,
  },
  data() {
    return {
      orderId: null,
      overview: null,
      loading: false,
      failed: false,
      labelLoadingId: null,
      openHistory: {},
      pollTimer: null,
      pollCount: 0,
    };
  },
  computed: {
    ...mapGetters(['selectedOrder']),
    ...mapState({
      currentUser: state => state.auth.user,
    }),
    shipments() {
      return this._.get(this.overview, 'shipments', []);
    },
    items() {
      return this._.get(this.overview, 'items', []);
    },
    // DP-5377: Canada lists the most recent shipment first.
    orderedShipments() {
      return this.shipments.some(shipment => shipment.is_manual_entry)
        ? [...this.shipments].reverse()
        : this.shipments;
    },
    // DP-5377: Canada staff add tracking URLs (any number of them) instead of
    // creating labels.
    canManageCanada() {
      return this.staffCountryCode === 'CA';
    },
    pendingItems() {
      return this.items.filter(item => !item.dispatched);
    },
    staffCountryCode() {
      return this._.get(this.currentUser, 'outlet.region.country_code');
    },
    // Same rule as the Create Shipping Order button under the job: no shipping
    // from New Zealand outlets (IOGO-238). Canada staff use Add Tracking / Add
    // Another Shipment instead (DP-5377).
    canCreateShippingOrder() {
      if (!this.overview) return false;

      return this.staffCountryCode !== 'NZ' && this.staffCountryCode !== 'CA';
    },
    // Not `timezone`: main.js's global mixin already defines a `timezone` data
    // property (the browser's), and Vue keeps that one over a computed.
    orderTimezone() {
      const regionId = this._.get(this.selectedOrder, 'origin_outlet.region_id');

      return regionId === 5 || regionId === 13 ? 'America/Toronto' : 'Australia/Sydney';
    },
  },
  created() {
    this.$root.$on('shipmentsChanged', this.onShipmentsChanged);
  },
  beforeDestroy() {
    this.$root.$off('shipmentsChanged', this.onShipmentsChanged);
    clearTimeout(this.pollTimer);
  },
  methods: {
    onShipmentsChanged(orderId, overview) {
      if (this.overview && orderId === this.orderId) {
        this.pollCount = 0;
        if (overview) {
          this.overview = overview;
          this.pollWhileSending();
        } else {
          this.load();
        }
      }
    },
    onShow() {
      this.orderId = this._.get(this.selectedOrder, 'id');
      this.overview = null;
      this.load();
    },
    onHidden() {
      this.overview = null;
      this.labelLoadingId = null;
      this.openHistory = {};
      clearTimeout(this.pollTimer);
    },
    async load() {
      if (!this.orderId) return;

      this.loading = true;
      this.failed = false;
      try {
        const response = await apiOrders.getOrderShipments(this.orderId);
        this.overview = response.data;
        this.pollWhileSending();
      } catch (error) {
        this.failed = true;
      } finally {
        this.loading = false;
      }
    },
    // DP-5377: wc-api's queue sends the email a few seconds after it is saved;
    // reload until "Sending" turns into Sent or Failed (up to a minute).
    pollWhileSending() {
      clearTimeout(this.pollTimer);
      const sending = this.shipments.some(shipment => shipment.email_status === 'queued');
      if (!sending || this.pollCount >= 15) return;

      this.pollCount += 1;
      this.pollTimer = setTimeout(() => {
        if (this.overview) this.load();
      }, 4000);
    },
    openCa(mode, shipment = null) {
      this.$root.$emit('openCaShipment', { mode, orderId: this.orderId, shipment });
    },
    toggleHistory(shipmentId) {
      this.$set(this.openHistory, shipmentId, !this.openHistory[shipmentId]);
    },
    labelButtonText(shipment) {
      if (shipment.cargo_company === 'AUPOST') return 'Get Label';

      return shipment.is_manual_entry ? 'View Tracking' : 'Tracking';
    },
    // DP-5597: Sent shows when it was sent; Failed shows when the last attempt
    // failed, not the earlier successful send.
    emailDate(shipment) {
      if (shipment.email_status === 'sent') return shipment.email_sent_at;
      if (shipment.email_status !== 'failed') return null;

      const failed = (shipment.events || []).filter(event => event.type === 'email_failed');

      return failed.length ? failed[failed.length - 1].created_at : null;
    },
    // DP-5650: one line with the date, as in the ticket design ("Email sent – 17 Oct 2026").
    emailStatusText(shipment) {
      const texts = { sent: 'Email sent', not_sent: 'Email not sent', queued: 'Sending email…', failed: 'Email failed' };
      const text = texts[shipment.email_status] || shipment.email_status;
      const date = this.emailDate(shipment);

      return date ? `${text} – ${this.formatDate(date)}` : text;
    },
    eventText(event) {
      const by = event.user ? ` by ${event.user}` : '';
      const kind = { initial: 'Order Is Dispatched', additional: 'Additional Shipment', updated: 'Updated Tracking' }[
        event.email_kind
      ];
      const notes = event.notes ? ` — ${event.notes}` : '';

      switch (event.type) {
        case 'created':
          return `Shipment added${by}: ${event.new_tracking_id}${notes}`;
        case 'tracking_edited':
          return `Tracking changed${by} from ${event.old_tracking_id} to ${event.new_tracking_id}${notes}`;
        case 'email_queued':
          return `${kind} email requested${by} to ${event.email}`;
        case 'email_sent':
          return `${kind} email sent to ${event.email}`;
        case 'email_failed':
          return `${kind} email FAILED${event.email ? ` to ${event.email}` : ''}: ${event.error}`;
        default:
          return event.type;
      }
    },
    formatDate(value) {
      return this.$moment(value)
        .tz(this.orderTimezone)
        .format('D MMM YYYY, HH:mm');
    },
    details(item) {
      return itemDetails(item);
    },
    creatorName(shipment) {
      return this._.get(shipment, 'created_by.name') || '—';
    },
    // "20 Jul, 15:30", in the order's own timezone like the rest of the job.
    createdAt(shipment) {
      if (!shipment.created_at) return '—';

      return this.$moment(shipment.created_at)
        .tz(this.orderTimezone)
        .format('D MMM, HH:mm');
    },
    async openLabel(shipment) {
      // Opened inside the click: a tab opened after the request returns is
      // blocked as a popup (same reason as Print in JobsTable).
      const tab = window.open('', '_blank');
      this.labelLoadingId = shipment.id;
      try {
        const { data } = await apiOrders.getOrderShipmentLabel(shipment.id);
        if (!data.url) {
          if (tab) tab.close();
          this.$store.dispatch('showErrorAlert', 'This shipment has no label to open.');
          return;
        }
        if (tab) {
          tab.location = data.url;
        } else {
          window.location.href = data.url;
        }
      } catch (error) {
        if (tab) tab.close();
        this.$store.dispatch(
          'showErrorAlert',
          this._.get(error, 'response.data.message') || 'Unable to get the label. Please try again.',
        );
      } finally {
        this.labelLoadingId = null;
      }
    },
    // Opens the same Create Shipping Order popup as the button under the job.
    // Manage Shipping is only reachable from an expanded job, and expanding it
    // already loaded the full order (items, customer, addresses), so the popup
    // opens straight away with that order. Reloading it first made the click
    // look dead for several seconds. Only an order that is somehow not loaded
    // falls back to the jobs table, which reloads it before opening.
    createShippingOrder() {
      const { orderId } = this;
      const order = this.selectedOrder;
      this.$bvModal.hide('manage-shipping-modal');

      if (order && order.id === orderId && order.item_groups && order.customer) {
        this.$store.dispatch('addressModal/openCreateShippingOrderModal', { order, vm: this });
        return;
      }

      this.$root.$emit('openCreateShippingOrder', orderId);
    },
  },
};
</script>

<style scoped>
/* Space between the last list and the sticky grey footer. */
.dp-ms-body {
  padding-bottom: 24px;
}
.dp-ms-loading {
  color: #718098;
  padding: 24px 0;
}
.dp-ms-heading {
  font-size: 24px;
  font-weight: 700;
  color: #292a2d;
  margin: 12px 0 16px;
}
.dp-ms-divider {
  border-top: 1px solid #e4e7ec;
  margin: 24px 0 12px;
}
.dp-ms-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #fafafa;
  border: 1px solid #f4f4f5;
  border-radius: 8px;
  padding: 24px 12px;
  font-size: 14px;
  color: #292a2d;
}
.dp-ms-card {
  background: #fafafa;
  border: 1px solid #f4f4f5;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 8px;
}
.dp-ms-card-title {
  font-size: 16px;
  font-weight: 700;
  color: #2a68f5;
  padding: 0 12px 4px;
}
.dp-ms-list {
  border: 1px solid #d9dfe8;
  border-radius: 8px;
  padding: 8px 12px;
}
.dp-ms-row {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 12px;
}
.dp-ms-item {
  flex: 1 1 auto;
  min-width: 0;
}
.dp-ms-item-name {
  font-size: 14px;
  color: #000;
}
.dp-ms-qty {
  color: #a9aaab;
  margin-left: 5px;
}
.dp-ms-item-details,
.dp-ms-item-id {
  font-size: 12px;
  color: #a9aaab;
}
.dp-ms-pill {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  padding: 8px 10px;
  border-radius: 100px;
  white-space: nowrap;
}
.dp-ms-pill--dispatched {
  background: #5ac072;
  color: #fff;
}
.dp-ms-pill--pending {
  background: #edf0f4;
  color: #718098;
}
.dp-ms-ca-email {
  flex: 0 0 auto;
}
.dp-ms-ca-email-status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #718098;
  white-space: nowrap;
}
.dp-ms-ca-email-status svg {
  flex: 0 0 auto;
}
.dp-ms-ca-email-status--sent {
  color: #2f9e4f;
}
.dp-ms-ca-email-status--failed {
  color: #e5484d;
}
.dp-ms-ca {
  border-top: 1px solid #edf0f4;
  margin: 4px 12px 0;
  padding-top: 12px;
}
.dp-ms-ca-facts {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
}
.dp-ms-ca-notes {
  flex: 1 1 200px;
}
.dp-ms-ca-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}
.dp-ms-ca-date {
  font-size: 12px;
  color: #a9aaab;
  margin-right: 6px;
}
.dp-ms-ca-history {
  list-style: none;
  padding: 8px 0 0;
  margin: 0;
  font-size: 13px;
}
.dp-ms-ca-history li {
  padding: 4px 0;
  word-break: break-word;
}
.dp-ms-meta {
  flex: 0 0 140px;
}
.dp-ms-meta-label {
  font-size: 12px;
  color: #a9aaab;
}
.dp-ms-meta-value {
  font-size: 14px;
  color: #000;
}
.dp-ms-label-btn,
.dp-ms-close {
  font-size: 14px;
  font-weight: 500;
  color: #292a2d;
  border: 1px solid #b1bfd5;
  border-radius: 6px;
  background: #fff;
}
.dp-ms-label-btn {
  flex: 0 0 auto;
}
.dp-ms-close {
  padding: 12px 24px;
}
.dp-ms-create {
  display: inline-flex;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  border-radius: 6px;
  padding: 12px;
}
@media (max-width: 720px) {
  .dp-ms-row {
    flex-wrap: wrap;
    gap: 12px;
  }
  .dp-ms-item {
    flex-basis: 100%;
  }
}
</style>
